import { useEffect } from 'react';

import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { useTranslations } from 'next-intl';

import { BagError } from '../BagError';
import { BagSignupPrompt } from '../BagSignupPrompt';
import { useBagSignupPromptExperiment } from '../BagSignupPrompt/useBagSignupPromptExperiment';
import { QuickAccessBagSeller } from '../QuickAccessBagSeller';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { EmptyMessage } from '@/components/EmptyMessage';
import { ExtendedLink } from '@/components/ExtendedLink';
import { generateSellerKey } from '@/modules/bag/helpers';
import { useBagDetailed } from '@/modules/bag/useBagDetailed';
import { useBagTracking } from '@/modules/bag/useBagTracking';
import { useBagProductNudges } from '@/modules/nudges/hooks/useBagProductNudges';

function QuickAccessBag() {
  const t = useTranslations('bag');
  const {
    query: { data, isLoading, isError },
  } = useBagDetailed({ enabled: true, includeOffers: true });

  const bagTracking = useBagTracking();

  const { sellers } = data || {};
  const { nudgesBySeller } = useBagProductNudges(sellers);
  const showSignupPrompt = useBagSignupPromptExperiment(sellers);

  function renderContent() {
    if (isLoading) {
      return <LoadingBall />;
    }
    if (isError) {
      return <BagError />;
    }
    if (sellers?.length !== 0) {
      return (
        <>
          {showSignupPrompt && (
            <BagSignupPrompt
              id="quick-access-bag-sign-up-panel"
              className={styles.signupPanel}
              isQuickAccess
            />
          )}
          <ol>
            {sellers?.map((data, index) => {
              const isLastItem = index + 1 === sellers.length;
              const sellerKey = generateSellerKey(data);

              return (
                <li className={styles.listItem} key={sellerKey}>
                  <QuickAccessBagSeller
                    seller={data.seller}
                    banners={data.banners}
                    products={data.products}
                    summary={data.summary}
                    nudgesData={nudgesBySeller[sellerKey] || {}}
                  />
                  {!isLastItem && <div className={styles.line} />}
                </li>
              );
            })}
          </ol>
        </>
      );
    }
    if (sellers?.length === 0) {
      return (
        <EmptyMessage
          title={t('Bag.QuickAccess.Empty.Title')}
          text={t('Bag.QuickAccess.Empty.Subtitle')}
          cta={
            <ExtendedLink href="/explore">
              <ButtonExperiment>
                {t('Bag.QuickAccess.Empty.Cta')}
              </ButtonExperiment>
            </ExtendedLink>
          }
        />
      );
    }
    return null;
  }

  useEffect(() => {
    bagTracking.sendQuickAccessBagViewEvent();
  }, []);

  function handleViewBagClick() {
    bagTracking.sendBagViewAction('quickAccessBag');
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>{renderContent()}</div>
      <div className={styles.ctaWrapper}>
        <ExtendedLink href="/bag">
          <ButtonExperiment
            variant="secondary"
            fullWidth
            onClick={handleViewBagClick}
          >
            {t('Bag.QuickAccess.ViewBag')}
          </ButtonExperiment>
        </ExtendedLink>
      </div>
    </div>
  );
}

export { QuickAccessBag };
